"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import {
  DARK_BAND,
  GradText,
  GridRowCenter,
  SectionHead,
  SectionShell,
  Terminal,
  useBandRowAlign,
  useRefGridMode,
  useWholeRowBlocks,
} from "./kit";
import { EASE_OUT, gsap, insure, NOT_REDUCED, useEnterStagger, useGSAP } from "./kit/motion";
import { routePath } from "@/lib/routes";

function UcList({ items, light = false }: { items: string[]; light?: boolean }) {
  return (
    <ul className={`flex flex-col gap-[13px] ${light ? "text-white/85" : ""}`}>
      {items.map((i) => (
        <li key={i} className="flex items-start gap-3 text-[15px]">
          <span className={`mt-0.5 shrink-0 ${light ? "text-brand-500" : "text-brand-600"}`}>✓</span>
          {i}
        </li>
      ))}
    </ul>
  );
}

function RouteNode({
  tone,
  title,
  sub,
  pct,
  pctTone,
}: {
  tone: string;
  title: string;
  sub: string;
  pct: string;
  pctTone: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line-2 bg-surface px-4 py-3.5">
      <div className={`grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] text-base ${tone}`}>◎</div>
      <div className="min-w-0 flex-1">
        <div className="text-[14.5px] font-semibold">{title}</div>
        <div className="font-jetbrains text-[12.5px] text-muted">{sub}</div>
      </div>
      <div className={`font-jetbrains text-[13px] font-semibold ${pctTone}`}>{pct}</div>
    </div>
  );
}

const MarketingVisual = (
  <div className="flex min-h-[300px] flex-col gap-3 rounded-ref-lg border border-line bg-surface p-6 shadow-ref">
    <RouteNode tone="bg-brand-600/[0.08] text-brand-600" title="spring/newsletter" sub="12,408 clicks · 62% mobile" pct="51%" pctTone="text-data-green" />
    <RouteNode tone="bg-data-blue/[0.12] text-data-blue" title="spring/instagram" sub="8,941 clicks · 71% mobile" pct="36%" pctTone="text-data-green" />
    <RouteNode tone="bg-data-green/[0.12] text-data-green" title="spring/billboard-qr" sub="3,204 scans · 41% mobile" pct="13%" pctTone="text-data-amber" />
  </div>
);

const DEV_CODE = `# POST /v1/links
import requests
r = requests.post("https://api.rii.link/v1/links",
  json={
    "url": "https://myshop.com/web",
    "slug": "summer-sale",
    "routing": [{"if": {"device": "ios"},
                 "to": "https://apps.apple.com/app/id123"}]
  }, headers={"Authorization": "Bearer rii_live_…"})
→ 201 Created · rii.link/summer-sale`;

const AI_CODE = `// agent calls a rii MCP tool
{
  "tool": "analytics_summary",
  "args": { "period": "7d" },
  "returns": {
    "totals": { "requests": 18423 },
    "breakdowns": {
      "country": [{ "key": "DE", "count": 7012 }]
    }
  }
}`;

interface Tab {
  id: string;
  label: string;
  title: string;
  desc: string;
  list: string[];
  visual: ReactNode;
  /** Dedicated page for this persona + its CTA label (shown when `linked`). */
  href: string;
  cta: string;
}

const TABS: Tab[] = [
  {
    id: "mkt",
    label: "Marketing teams",
    title: "Track & route every campaign",
    desc: "Branded links and dynamic QR for every channel, with UTM and referrer breakdowns to see what worked.",
    list: [
      "One link per campaign, routed by device, geo & language",
      "Campaign-tag & referrer breakdowns for every click",
      "Editable QR on print — never reprint a flyer again",
      "QR scans and link clicks split in one dashboard",
    ],
    visual: MarketingVisual,
    href: routePath("solutionsMarketing"),
    cta: "Explore marketing solutions",
  },
  {
    id: "dev",
    label: "Developers",
    title: "Links as infrastructure",
    desc: "A fast, predictable REST API with a live event stream. Wire links into your product in minutes.",
    list: [
      "Create & route links programmatically at scale",
      "Live click & scan stream (SSE) for API-created links",
      "App-store & device routing out of the box",
      "Millisecond redirects straight from memory",
    ],
    visual: (
      <Terminal path="create-link.py" minHeight={300}>
        <pre className="m-0 whitespace-pre-wrap font-[inherit]">{DEV_CODE}</pre>
      </Terminal>
    ),
    href: routePath("docs"),
    cta: "Read the developer docs",
  },
  {
    id: "ai",
    label: "AI & LLM apps",
    title: "Feed agents structured data",
    desc: "Give LLMs and tools a stable URL that returns clean JSON — no scraping, no redirect parsing.",
    list: [
      "Serve JSON, HTML or a redirect — per rule, from one link",
      "Change the payload anytime — the URL stays stable",
      "Serve app configs & feature flags from a link",
      "Manage everything over MCP",
    ],
    visual: (
      <Terminal path="agent · tool_call" minHeight={300}>
        <pre className="m-0 whitespace-pre-wrap font-[inherit]">{AI_CODE}</pre>
      </Terminal>
    ),
    href: routePath("aiMcp"),
    cta: "See rii for AI & agents",
  },
];

/** `linked` shows a per-persona CTA to its dedicated page — passed only on the
 *  homepage highlights reel, not on the pages that also embed this section.
 *  `band` picks the grid-mode beat: "dark" (homepage) or "none" for light
 *  pages whose rhythm already has strong bands adjacent (e.g. /smart-routing). */
export default function UseCasesSection({
  linked = false,
  band = "dark",
}: {
  linked?: boolean;
  band?: "dark" | "none";
}) {
  const [tab, setTab] = useState("mkt");
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];
  const onGrid = useRefGridMode();
  // Dark beat in the homepage's light/dark alternation; light elsewhere.
  const dark = onGrid && band === "dark";

  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid
  const mounted = useRef(false);

  // useWholeRowBlocks/useBandRowAlign pad and re-align [data-grid-block]s for
  // grid-paper layout — running them on /solutions/marketing (non-grid
  // RefSections) would inject inline padding/margins into this SHARED section
  // and break "visually unchanged". Hooks can't be called conditionally, so
  // gate them by ref instead: both bail immediately once `innerRef.current` is
  // null, so pointing them at `nullRef` (never attached) off-grid is a full
  // no-op rather than a skipped hook call. Default breathing row on the main
  // panel: text column + visual, not one enclosing opaque card.
  useWholeRowBlocks(onGrid ? panelRef : nullRef);
  useBandRowAlign(onGrid ? panelRef : nullRef);

  // Entrance: tab pills pop in, then the two panel columns rise.
  useEnterStagger(bodyRef, "[data-uc-tab]", { y: 12, duration: 0.5, stagger: 0.08 });
  useEnterStagger(bodyRef, "[data-uc-panel] > *", { y: 30, stagger: 0.12, delay: 0.15 });

  // Tab switch: quick content swap — copy column and visual settle back in.
  useGSAP(
    () => {
      if (!mounted.current) {
        mounted.current = true;
        return;
      }
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        insure(
          gsap.fromTo(
            panelRef.current!.children,
            { y: 14, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.45,
              ease: EASE_OUT,
              stagger: 0.07,
              overwrite: "auto",
              clearProps: "transform,opacity",
            },
          ),
        );
      });
    },
    { dependencies: [tab], scope: panelRef },
  );

  return (
    <SectionShell
      divider
      band={dark ? "dark" : undefined}
      decoAvoid
      className={dark ? "max-md:bg-[#14141c]" : ""}
    >
      <SectionHead
        center
        eyebrow="Built for your team"
        tone={dark ? "light" : undefined}
        gridFill={dark ? DARK_BAND : undefined}
        title={
          <>
            One routing layer, <GradText>three superpowers.</GradText>
          </>
        }
      />

      <div ref={bodyRef}>
        {/* Row-snapped so the pills sit vertically centered in a grid row. */}
        <GridRowCenter className="mb-10">
          {/* `data-snug`: GridRowCenter already row-snaps this floater to a
              single whole cell (height + top-line margin on its own wrapper)
              — the default +1 breathing row would double that growth when
              useWholeRowBlocks pads this block on top of it. */}
          <div
            data-grid-block
            data-grid-fill={dark ? DARK_BAND : undefined}
            data-snug
            className="mx-auto flex w-fit flex-wrap justify-center gap-2"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                data-uc-tab
                type="button"
                onClick={() => setTab(t.id)}
                className={`rounded-[11px] border px-5 py-[11px] text-[15px] font-semibold transition-colors ${
                  t.id === tab
                    ? "border-transparent bg-brand-600 text-white"
                    : dark
                      ? "border-white/15 bg-white/[0.07] text-white/75 hover:border-white/30 hover:text-white"
                      : "border-line bg-surface text-muted hover:border-line-2 hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </GridRowCenter>

        <div
          ref={panelRef}
          data-grid-block
          data-grid-fill={dark ? DARK_BAND : undefined}
          data-uc-panel
          className="grid items-center gap-[46px] md:grid-cols-2"
        >
          <div>
            <h3
              className={`mb-3.5 text-[clamp(24px,2.6vw,32px)] font-bold tracking-[-0.02em] ${
                dark ? "text-white" : ""
              }`}
            >
              {active.title}
            </h3>
            <p className={`mb-[22px] text-base ${dark ? "text-white/70" : "text-muted"}`}>{active.desc}</p>
            <UcList items={active.list} light={dark} />
            {linked && (
              <a
                href={active.href}
                className={`group mt-7 inline-flex items-center gap-1.5 text-[15px] font-semibold ${
                  dark ? "text-brand-500" : "text-brand-600"
                }`}
              >
                {active.cta}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
            )}
          </div>
          <div>{active.visual}</div>
        </div>
      </div>
    </SectionShell>
  );
}
